import { Button, Modal, Select, Spin, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import { getUserId, useIsStudent } from "../../utils/utilFunctions";
import { getStudentById } from "../../Requests/academic-user-requests";
import { theme } from "../../theme";
import {
	getCurrentStudentStudyProgramInfo,
	getStudentAccountInfo,
	getStudyProgramsInfo,
} from "../../utils/reactQueriesConstants";
import {
	getAvailableStudyProgramsForStudent,
	getCurrentStudentStudyProgram,
	getStudyProgramsForFaculties,
	setCurrentStudentStudyProgram,
} from "../../Requests/student-study-requests";
import CustomSelect from "../../CustomComponents/CustomSelect";
import styles from "./SelectStudyProgramModal.module.scss";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Notifications/NotificationsUtils";
import {
	StudentStudyProgramDTO,
	StudentYearEnum,
	StudyProgramDTO,
} from "../../Api";
import { userYearsBachelor, userYearsMaster } from "../../utils/constants";

const { Option } = Select;

const selectableStudyYears = userYearsBachelor.concat(userYearsMaster);

const hasRealStudentStudyYear = (year?: StudentYearEnum) =>
	year != null && year !== StudentYearEnum.NotApplicable;

/** Mirrors BE GetStudyCycleFromStudentYear + GetStudyProgramToDTOSpec study-cycle filter. */
function getStudyCycleFromStudentYear(
	studentYear: StudentYearEnum
): string | null {
	switch (studentYear) {
		case StudentYearEnum.L1:
		case StudentYearEnum.L2:
		case StudentYearEnum.L3:
		case StudentYearEnum.L4:
			return "Bachelor";
		case StudentYearEnum.M1:
		case StudentYearEnum.M2:
			return "Master";
		default:
			return null;
	}
}

function filterStudyProgramsByStudentYear(
	programs: StudyProgramDTO[],
	year: StudentYearEnum
): StudyProgramDTO[] {
	const studyCycle = getStudyCycleFromStudentYear(year);
	if (!studyCycle) {
		return [];
	}
	const cycleLower = studyCycle.toLowerCase();
	return programs.filter(
		(p) =>
			p.studyCycle != null && p.studyCycle.trim().toLowerCase() === cycleLower
	);
}

const SelectStudyProgramModal = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [spinning, setSpinning] = useState(false);
	const isStudent = useIsStudent();
	const [studyProgram, setStudyProgram] = useState("");
	const [draftYear, setDraftYear] = useState<StudentYearEnum | undefined>(
		undefined
	);
	const [currentStudyProgram, setCurrentStudyProgram] = useState<
		StudentStudyProgramDTO | undefined | "NONE"
	>(undefined);
	const [isModalVisible, setIsModalVisible] = useState(false);

	const openDataErrorNotification = (_error: any) => {
		openNotification(
			t("account.error"),
			t("account.fetchUserDataError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const saveChangesError = () => {
		openNotification(
			t("account.saveError"),
			t("account.saveChangesError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const successMessage = () => {
		openNotification(
			t("account.success"),
			t("account.successSaveMessage"),
			NOTIFICATION_TYPES.SUCCESS
		);
	};

	const { data: studentState } = useQuery(
		[getStudentAccountInfo, isStudent],
		() => {
			if (isStudent) return getStudentById(getUserId());
		},
		{
			onError: openDataErrorNotification,
			refetchOnWindowFocus: false,
		}
	);

	const profileNeedsYear =
		!!studentState && !hasRealStudentStudyYear(studentState.year);

	const yearReady = profileNeedsYear
		? draftYear != null && hasRealStudentStudyYear(draftYear)
		: hasRealStudentStudyYear(studentState?.year);

	const effectiveYearForPrograms: StudentYearEnum | undefined = profileNeedsYear
		? draftYear
		: studentState?.year;

	const { data: studyPrograms } = useQuery(
		[
			getStudyProgramsInfo,
			isStudent,
			studentState?.facultyId,
			studentState?.year,
			draftYear,
			profileNeedsYear,
			isModalVisible,
		],
		async () => {
			if (
				!isStudent ||
				!isModalVisible ||
				!studentState ||
				!effectiveYearForPrograms ||
				!hasRealStudentStudyYear(effectiveYearForPrograms)
			) {
				return null;
			}
			if (profileNeedsYear) {
				if (!studentState.facultyId || !draftYear) return null;
				const all = await getStudyProgramsForFaculties([
					studentState.facultyId,
				]);
				return filterStudyProgramsByStudentYear(all, draftYear);
			}
			return await getAvailableStudyProgramsForStudent();
		},
		{
			onError: (err) => {
				openDataErrorNotification(err);
			},
			refetchOnWindowFocus: false,
		}
	);

	useQuery(
		[getCurrentStudentStudyProgramInfo, isStudent],
		() => {
			if (isStudent) return getCurrentStudentStudyProgram();
		},
		{
			onSuccess(data) {
				setCurrentStudyProgram(data);
			},
			retry(failureCount, error: any) {
				if (error.status === 404) {
					setCurrentStudyProgram("NONE");
					return false;
				}
				return failureCount < 3;
			},

			refetchOnWindowFocus: false,
		}
	);

	useEffect(() => {
		if (!isModalVisible) {
			setDraftYear(undefined);
			setStudyProgram("");
		}
	}, [isModalVisible]);

	const handleOk = async () => {
		setSpinning(true);

		try {
			const yearToSave: StudentYearEnum | undefined = profileNeedsYear
				? draftYear
				: studentState?.year;

			if (profileNeedsYear) {
				if (!draftYear) {
					saveChangesError();
					return;
				}
			} else if (!hasRealStudentStudyYear(studentState?.year)) {
				saveChangesError();
				return;
			}

			if (studyProgram === undefined || studyProgram === "") {
				saveChangesError();
				return;
			}

			await setCurrentStudentStudyProgram(studyProgram, yearToSave);
			await queryClient.invalidateQueries([getStudentAccountInfo, isStudent]);
			await queryClient.invalidateQueries(["getStudent", isStudent]);
			await queryClient.invalidateQueries([
				getCurrentStudentStudyProgramInfo,
				isStudent,
			]);
			await queryClient.invalidateQueries([getStudyProgramsInfo]);
			setIsModalVisible(false);
			setDraftYear(undefined);
			setStudyProgram("");
			successMessage();
		} catch {
			saveChangesError();
		} finally {
			setSpinning(false);
		}
	};

	const changeSelectedStudyProgram = async (studyProgramId: string) => {
		setStudyProgram(studyProgramId);
	};

	useEffect(() => {
		const modalVisibility =
			!!isStudent &&
			isStudent &&
			currentStudyProgram === "NONE" &&
			!!studentState &&
			!studentState.isExternal &&
			!!studentState.year &&
			![
				StudentYearEnum.D1,
				StudentYearEnum.D2,
				StudentYearEnum.D3,
				StudentYearEnum.D4,
				StudentYearEnum.D5,
			].includes(studentState?.year);

		setIsModalVisible(modalVisibility);
	}, [currentStudyProgram, isStudent, studentState?.year]);

	return isStudent ? (
		<div style={{ display: "flex", alignItems: "center" }}>
			<Modal
				open={isModalVisible}
				onOk={handleOk}
				title={t("studyProgramModal.studyProgram")}
				closable={false}
				width={"50%"}
				footer={
					<Button
						key="submit"
						type="primary"
						loading={spinning}
						disabled={
							spinning ||
							!yearReady ||
							studyProgram === undefined ||
							studyProgram === ""
						}
						onClick={handleOk}
						style={{
							background: theme.secondColor,
							border: theme.secondColor,
							borderRadius: "10px",
						}}
					>
						{t("studyProgramModal.save")}
					</Button>
				}
			>
				<Spin spinning={spinning}>
					<div>
						<Typography>{t("studyProgramModal.text")}</Typography>

						{profileNeedsYear ? (
							<>
								<Typography style={{ marginTop: 12 }}>
									{t("studyProgramModal.textMissingYear")}
								</Typography>
								<Select
									className={styles.customInput}
									style={{
										width: "100%",
										textAlign: "left",
										marginTop: "15px",
									}}
									placeholder={t("account.year")}
									value={draftYear}
									onChange={(y) => {
										setDraftYear(y as StudentYearEnum);
										setStudyProgram("");
									}}
								>
									{selectableStudyYears.map((year) => (
										<Option key={year} value={year}>
											{t("account." + year)}
										</Option>
									))}
								</Select>
							</>
						) : null}

						<CustomSelect
							key={`study-program-select-${String(
								effectiveYearForPrograms ?? "none"
							)}`}
							allowClear
							className={styles.customInput}
							style={{ width: "100%", textAlign: "left", marginTop: "15px" }}
							placeholder={t("account.studyProgram")}
							disabled={!yearReady}
							value={studyProgram || undefined}
							onChange={changeSelectedStudyProgram}
						>
							{studyPrograms?.map((studyProgram) => (
								<Option key={studyProgram.id} value={studyProgram.id}>
									{studyProgram.name}
								</Option>
							))}
						</CustomSelect>
					</div>
				</Spin>
			</Modal>
		</div>
	) : null;
};

export default SelectStudyProgramModal;
